import React, { useState } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, 
  TextField, Stack, Box, Typography, InputAdornment, 
  CircularProgress, Divider, Grid
} from '@mui/material';
import { 
  Fingerprint, AppRegistration, Badge, Smartphone, 
  CheckCircleOutline, ErrorOutline 
} from '@mui/icons-material';

export const DeviceRegistrationDialog = ({ open, onClose, organization, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    device_id: '',
    imei: '',
    device_label: '',
    manufacturer: '',
    model: '',
    os_version: '11'
  });

  // ─── VALIDATION LOGIC ───
  // IMEI must be exactly 15 digits
  const isImeiValid = /^\d{15}$/.test(formData.imei);
  const showImeiError = formData.imei.length > 0 && !isImeiValid;

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Prevent non-numeric input for IMEI
    if (name === 'imei' && value !== '' && !/^\d+$/.test(value)) return;
    // Limit IMEI to 15 chars
    if (name === 'imei' && value.length > 15) return;

    setFormData({ ...formData, [name]: value });
  };

  const handleRegister = async () => {
    if (!isImeiValid) return; // Block submission if invalid
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://console.dreamtec.io/devices`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ 
          ...formData, 
          cid: organization.cid || organization.id 
        })
      });

      if (response.ok) {
        setFormData({ device_id: '', imei: '', device_label: '', manufacturer: '', model: '', os_version: '11' });
        onSuccess('Device registered successfully');
        onClose();
      } else {
        const err = await response.json();
        alert(err.detail || "Registration failed");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm" 
      PaperProps={{ sx: { borderRadius: '16px', backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ p: 3, pb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ bgcolor: 'rgba(47, 114, 124, 0.1)', p: 1, borderRadius: '10px', display: 'flex' }}>
            <AppRegistration sx={{ color: '#2F727C' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#1A3A3F', lineHeight: 1.2 }}>
              Register Handheld
            </Typography>
            <Typography variant="caption" sx={{ color: '#2F727C', fontWeight: 700 }}>
              FOR: {organization.name || organization.id}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, py: 1 }}>
        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          
          {/* IDENTIFICATION */}
          <Grid item xs={12}>
            <Stack direction="row" spacing={2}>
              <TextField 
                label="Device ID" 
                name="device_id"
                fullWidth 
                size="small"
                value={formData.device_id} 
                onChange={handleChange}
                placeholder="Hardware ID"
                InputProps={{ startAdornment: <InputAdornment position="start"><Fingerprint sx={{ fontSize: 18 }} /></InputAdornment> }} 
              />
              <TextField 
                label="IMEI (15 Digits)" 
                name="imei"
                required
                fullWidth 
                size="small"
                value={formData.imei} 
                onChange={handleChange}
                error={showImeiError}
                helperText={showImeiError ? "Must be exactly 15 digits" : ""}
                placeholder="012345678901234"
                InputProps={{ 
                    endAdornment: formData.imei.length === 15 && isImeiValid ? (
                        <InputAdornment position="end"><CheckCircleOutline sx={{ color: '#10b981', fontSize: 18 }} /></InputAdornment>
                    ) : null 
                }}
              />
            </Stack>
          </Grid>

          {/* ASSET NAME */}
          <Grid item xs={12}>
            <TextField 
              label="SOTI Name" 
              name="device_label"
              fullWidth 
              size="small"
              value={formData.device_label} 
              onChange={handleChange}
              placeholder="SOTI Label"
              InputProps={{ startAdornment: <InputAdornment position="start"><Badge sx={{ fontSize: 18 }} /></InputAdornment> }} 
            />
          </Grid>

          <Grid item xs={12}><Divider sx={{ borderStyle: 'dashed', my: 1 }} /></Grid>

          {/* HARDWARE */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Manufacturer" name="manufacturer" fullWidth size="small" value={formData.manufacturer} onChange={handleChange} placeholder="e.g. Samsung" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Model" name="model" fullWidth size="small" value={formData.model} onChange={handleChange} placeholder="e.g. Galaxy Tab" />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  label="Android OS Version" 
                  name="os_version"
                  fullWidth
                  size="small"
                  value={formData.os_version} 
                  onChange={handleChange} 
                  InputProps={{ startAdornment: <InputAdornment position="start"><Smartphone sx={{ fontSize: 18 }} /></InputAdornment> }} 
                />
              </Grid>
            </Grid>
          </Grid>

        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
        <Button onClick={onClose} color="error">Cancel</Button>
        <Button 
          onClick={handleRegister} 
          variant="contained" 
          disabled={saving || !formData.device_id || !isImeiValid} 
          color="primary"
        >
          {saving ? <CircularProgress size={24} color="inherit" /> : 'Register'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};